// Partner-Pharmacies OCP parser. Extracts structured fields from a paste of the
// Ontario College of Pharmacists TCPR public register or a similar labelled
// pharmacy listing. Handles both labelled ("Pharmacy Name:", "Phone:") and
// address-block-only formats.
//
// Returns a subset of these keys:
//   name, address, city, province, postal_code, phone, fax, email,
//   ocp_license, contact_first_name, contact_last_name
//
// Uses the same underlying heuristics as /app/frontend/src/pages/Pharmacy.jsx
// (parsePharmacyLabel), extended for the additional reconciliation fields.

const cleanPhone = (s) =>
  s
    .replace(/[^\d+\-()\s.]/g, "")
    .replace(/^\s*\+?\s*1\s*(?=[\s\-.(])/, "")
    .replace(/\s+/g, " ")
    .replace(/\)\s*/, ") ")
    .trim();

const POSTAL_RE =
  /\b([ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z]\s?\d[ABCEGHJ-NPRSTV-Z]\d)\b/i;

const PROV_CODES = new Set([
  "AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU",
  "ON", "PE", "QC", "SK", "YT",
]);

export const parseOcpText = (raw) => {
  if (!raw) return {};
  const text = raw.replace(/[\u200B-\u200D\uFEFF]/g, "");
  const out = {};

  // ---------- Phone / fax ----------
  const phone =
    text.match(
      /(?:Location\s+)?(?:Tele)?phone(?:\s*(?:Number|#))?\s*[:#]?\s*([+\d()\-.\s]{7,})/i,
    ) || text.match(/\bTel\s*[:#]?\s*([+\d()\-.\s]{7,})/i);
  if (phone) out.phone = cleanPhone(phone[1]);

  const fax = text.match(
    /(?:Location\s+)?Fax(?:\s*(?:Number|#))?\s*[:#]?\s*([+\d()\-.\s]{7,})/i,
  );
  if (fax) out.fax = cleanPhone(fax[1]);

  // ---------- Email ----------
  const email = text.match(/\b([A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,})\b/);
  if (email) out.email = email[1];

  // ---------- OCP license / certificate ----------
  const lic = text.match(
    /(?:Certificate|Accreditation)(?:\s*Number|\s*No\.?|\s*#)?\s*[:#]?\s*(\d{3,7})/i,
  );
  if (lic) out.ocp_license = lic[1];

  // ---------- Lines and address ----------
  const lines = text
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  const looksLikeLabel =
    /^(Pharmacy\s+Name|Certificate|Accreditation|Manager|Category|Owner|Operator|General\s+Information|Community\s+pharmacy|Entitled)/i;

  const postalIdx = lines.findIndex((l) => POSTAL_RE.test(l));
  if (postalIdx >= 0) {
    const postalLine = lines[postalIdx]
      .replace(/^\s*(?:Location\s+)?Address\s*[:#]?\s*/i, "")
      .trim();
    const postalMatch = postalLine.match(POSTAL_RE);
    const postal = postalMatch
      ? postalMatch[1].toUpperCase().replace(/\s+/g, " ")
      : "";
    out.postal_code = postal;

    const remainder = postalLine.replace(POSTAL_RE, "").replace(/[,\s]/g, "");
    const postalOnItsOwn = remainder.length < 3;

    let cityLine = "";
    let streetLine = "";
    if (postalOnItsOwn && postalIdx > 0) {
      cityLine = lines[postalIdx - 1]
        .replace(/^\s*(?:Location\s+)?Address\s*[:#]?\s*/i, "")
        .trim();
      streetLine = postalIdx > 1 ? lines[postalIdx - 2].trim() : "";
    } else {
      cityLine = postalLine.replace(POSTAL_RE, "").replace(/,\s*$/, "").trim();
      streetLine =
        postalIdx > 0
          ? lines[postalIdx - 1]
              .replace(/^\s*(?:Location\s+)?Address\s*[:#]?\s*/i, "")
              .trim()
          : "";
    }
    if (streetLine && looksLikeLabel.test(streetLine)) streetLine = "";
    if (streetLine) out.address = streetLine;

    // Split cityLine into City + Province code
    if (cityLine) {
      const m = cityLine.match(/^(.+?)[,\s]+([A-Z]{2})\b/);
      if (m && PROV_CODES.has(m[2])) {
        out.city = m[1].trim().replace(/,$/, "").trim();
        out.province = m[2];
      } else {
        out.city = cityLine.replace(/,$/, "").trim();
      }
    }
  } else {
    const labelled = text.match(/(?:Location\s+)?Address\s*[:#]?\s*(.+)/i);
    if (labelled) out.address = labelled[1].trim();
  }

  // ---------- Pharmacy name ----------
  const nameLabel = text.match(/Pharmacy\s+Name\s*[:#]?\s*(.+)/i);
  if (nameLabel) {
    out.name = nameLabel[1].trim();
  } else {
    const skipRe =
      /^(Certificate|Accreditation|Category|Manager|Owner|Operator|Phone|Tel|Telephone|Fax|Address|Location|Hours|Website|Email|General\s+Information|Community\s+pharmacy|Entitled)/i;
    for (const line of lines) {
      if (skipRe.test(line)) continue;
      if (POSTAL_RE.test(line)) continue;
      if (/^[\d()\-.\s+]+$/.test(line)) continue;
      let cleaned = line
        .split(/Entitled\s+to\b/i)[0]
        .replace(
          /(Pharmacy|Drug\s*Mart|Drugs?|RX|Rexall|Shoppers|Guardian|IDA|PharmaChoice|Wellwise)Entitled.*$/i,
          "$1",
        )
        .trim();
      if (!cleaned) continue;
      out.name = cleaned;
      break;
    }
  }

  // ---------- Manager / Owner name → contact first / last ----------
  const mgr = text.match(
    /(?:Designated\s+)?Manager\s*[:#]?\s*(.+)/i,
  );
  const owner = text.match(/Owner\s*[:#]?\s*(.+)/i);
  const contactRaw = (mgr && mgr[1]) || (owner && owner[1]) || "";
  if (contactRaw) {
    // Strip suffixes like "R.Ph." / "Pharm.D." / ", R.Ph."
    const cleaned = contactRaw
      .replace(/,?\s*(R\.?\s*Ph\.?|Pharm\.?\s*D\.?)\s*/gi, "")
      .replace(/\s+/g, " ")
      .trim();
    // "Last, First" format?
    const m = cleaned.match(/^([^,]+),\s*(.+)$/);
    if (m) {
      out.contact_last_name = m[1].trim();
      out.contact_first_name = m[2].trim();
    } else {
      const parts = cleaned.split(/\s+/);
      if (parts.length >= 2) {
        out.contact_first_name = parts[0];
        out.contact_last_name = parts.slice(1).join(" ");
      } else {
        out.contact_first_name = cleaned;
      }
    }
  }

  return out;
};
